import './styles/App.css';
import './styles/Navigation.css';
import './styles/call.css';
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';

import { CallProvider } from './context/CallContext';
import { MeetingProvider } from './context/MeetingContext';
import { ScreenRecordingProvider } from './context/ScreenRecordingProvider';

// Pages
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CRM from './pages/CRM';
import CRMOutreach from './pages/CRMOutreach';
import { useCRMAccess } from './utils/crmAccess';
import Profile from './pages/User/Profile';
import Attendance from './pages/User/Attendance';
import Employees from './pages/Admin/Employees';
import AddEmployee from './pages/Admin/AddEmployee';
import Leaves from './pages/User/Leaves';
import Documents from './pages/User/Documents';
import EmployeeRequests from './pages/Admin/EmployeeRequests';
import WorkFromHome from './pages/User/WorkFromHome';
import CalendarPage from './pages/CalendarPage';
import Meetings from './pages/Meetings';
import MeetingRoom from './pages/MeetingRoom';
import MeetingJoinLink from './pages/MeetingJoinLink';
import GuestMeeting from './pages/GuestMeeting';
import AdminSettings from './pages/Admin/AdminSettings';
import WhatsAppAdmin from './pages/Admin/WhatsAppAdmin';
import UsageReport from './pages/Admin/UsageReport';
import Integrations from './pages/Admin/Integrations';
import AttendanceLogs from './pages/Admin/AttendanceLogs';
import AbsentEmployees from './pages/Admin/AbsentEmployees';
import EmployeeProfile from './pages/Admin/EmployeeProfile';
import EditEmployee from './pages/Admin/EditEmployee';
import Expenses from './pages/User/Expenses';
import AdminExpenses from './pages/Admin/AdminExpenses';
import AllExpenses from './pages/Admin/AllExpenses';
import RawPunches from './pages/Admin/RawPunches';
import AddExpense from './pages/User/AddExpense';
import EditExpense from './pages/User/EditExpense';
import AdminChat from './pages/Admin/AdminChat'
import Chats from './pages/Chats';
import Portal from './pages/Portal';
import GuestLogin from './pages/GuestLogin';
import GuestHome from './pages/GuestHome';
import ExternalUsers from './pages/ExternalUsers';
import Projects from './pages/Admin/Projects';
import ProjectWorkspace from './pages/Admin/ProjectWorkspace';
import MyProjects from './pages/User/MyProjects';
import MyProjectDetail from './pages/User/MyProjectDetail';
import Inventory from './pages/Admin/Inventory';
import MyInventory from './pages/User/MyInventory';
import AddInventory from './pages/Admin/AddInventory';
import EditInventory from './pages/Admin/EditInventory';
import Reimbursements from './pages/Admin/Reimbursements';
import Payroll from './pages/Admin/Payroll';
import Notifications from './pages/User/NotificationCenter';
import Tasks from './pages/Admin/Tasks';
import AddTask from './pages/Admin/AddTask';
import VoiceAssignTask from './pages/Admin/VoiceAssignTask';
import EditTask from './pages/Admin/EditTask';
import RecurringTaskDetail from './pages/Admin/RecurringTaskDetail';
import EditRecurringTask from './pages/Admin/EditRecurringTask';
import MyTasks from './pages/User/MyTasks';
import TaskDetail from './pages/TaskDetail';
import TaskReport from './pages/Admin/TaskReport';
import Performance from './pages/Admin/Performance';
import EmployeePerformance from './pages/Admin/EmployeePerformance';
import HelpTraining from './pages/User/HelpTraining';
import Academy from './pages/User/Academy';

// Components
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import ProtectedRoute from './components/ProtectedRoute';
import AlertCenter from './components/alerts/AlertCenter';
import VoiceMiniPlayer from './components/VoiceMiniPlayer';


// 👇 NEW: Impersonation Banner Component
const ImpersonationBanner = () => {
  const isImpersonating = localStorage.getItem('is_impersonating') === 'true';
  const currentUser = JSON.parse(localStorage.getItem('user'));

  if (!isImpersonating) return null;

  const handleReturnToAdmin = () => {
    // 1. Restore the Admin credentials
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');

    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
    }

    // 2. Clean up the backups
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    localStorage.removeItem('is_impersonating');

    // 3. Hard reload back to the Admin Employee list
    window.location.href = '/employees';
  };

  return (
    <div style={{
      background: '#dc2626',
      color: 'white',
      padding: '10px 20px',
      textAlign: 'center',
      zIndex: 9999,
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '15px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    }}>
      <span style={{ fontSize: '14px' }}>
        ⚠️ You are currently viewing the system as <strong>{currentUser?.name}</strong>.
      </span>
      <button
        onClick={handleReturnToAdmin}
        style={{
          padding: '6px 12px', background: 'light-dark(white, var(--theme-surface))', color: 'light-dark(#dc2626, var(--theme-danger-ink))',
          border: 'none', borderRadius: '4px', cursor: 'pointer',
          fontWeight: 'bold', fontSize: '13px', transition: '0.2s'
        }}
      >
        Return to Admin
      </button>
    </div>
  );
};

// Layout Component manages the mobile sidebar state
const DashboardLayout = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const shellRef = useRef(null);
  const { pathname } = useLocation();

  // Installed mobile PWAs can expose a layout viewport taller than the area
  // above the system navigation bar. Size the shell to the visible viewport
  // and keep its bottom tabs above any hidden portion of that viewport.
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const viewport = window.visualViewport;
    const shell = shellRef.current;
    if (!standalone || !viewport || !shell) return undefined;

    let frame;
    const updateViewport = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (viewport.height < 200) return;
        shell.style.setProperty('--pwa-visible-height', `${Math.round(viewport.height)}px`);
        shell.style.setProperty('--pwa-bottom-offset', `${Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop))}px`);
      });
    };
    updateViewport();
    viewport.addEventListener('resize', updateViewport);
    viewport.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', updateViewport);
      viewport.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      shell.style.removeProperty('--pwa-visible-height');
      shell.style.removeProperty('--pwa-bottom-offset');
    };
  }, []);

  useEffect(() => {
    let closingSelect = null;
    const onPointerDown = event => {
      closingSelect = null;
      if (!window.matchMedia('(max-width: 768px)').matches || !CSS.supports('appearance', 'base-select')) return;
      const select = document.querySelector('select:open');
      if (!select || getComputedStyle(select).appearance !== 'base-select') return;
      const rect = select.getBoundingClientRect();
      if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) closingSelect = select;
    };
    const onClick = event => {
      if (!closingSelect) return;
      if (event.type === 'click') closingSelect = null;
      // Touch light-dismisses on pointerup, before compatibility mousedown
      // activates the field again. Suppress that activation and its click.
      event.preventDefault();
      event.stopPropagation();
    };
    const onCancel = () => { closingSelect = null; };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mousedown', onClick, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('pointercancel', onCancel, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('mousedown', onClick, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('pointercancel', onCancel, true);
    };
  }, []);

  useEffect(() => { setSidebarOpen(false); }, [pathname]);
  useEffect(() => {
    const onEscape = event => { if (event.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  /*
   * CallProvider wraps the whole authenticated shell rather than the chat page.
   *
   * A call has to arrive while the user is looking at Attendance and survive
   * them navigating to Tasks mid-conversation. Mounted inside Chats.js it would
   * ring for nobody outside the chat module and tear the connection down the
   * moment they clicked away.
   */
  return (
    <CallProvider>
    {/* Same reasoning as CallProvider: a chat screen recording has to keep
        running when the user opens another chat or page, so it is owned here
        rather than by the message box that started it. */}
    <ScreenRecordingProvider>
    {/* A meeting in progress lives here too, for the same reason as a call: it
        has to survive the person moving between pages. Inside CallProvider so
        it can step aside when a call begins. */}
    <MeetingProvider>
    <div ref={shellRef} className={`authenticated-shell ${pathname.startsWith('/chats') ? 'shell-chat' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>

      <ImpersonationBanner />

      <div className="app-container" style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        {/* Background overlay for mobile drawer */}
        <div
          className={`mobile-navigation-backdrop ${isSidebarOpen ? 'visible' : ''}`}
          onClick={closeSidebar}
        />

        <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} onOpen={() => setSidebarOpen(true)} />

        <div className="content-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Topbar onToggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />

          <div className="main-content" style={{ flex: 1, overflowY: 'auto', paddingBottom: '30px' }}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* Same placement argument again, for the same reason: a new task or a
          direct message has to reach the person while they are looking at
          Attendance. Inside CallProvider so it can fall silent while a call
          is ringing, and inside the shell so it is never mounted for a
          logged-out visitor or the external portal. */}
      <AlertCenter />

      {/* A voice note keeps playing across pages (utils/voicePlayback); this
          is what shows it once its bubble is out of sight. */}
      <VoiceMiniPlayer />
    </div>
    </MeetingProvider>
    </ScreenRecordingProvider>
    </CallProvider>
  );
};

function App() {
  const isAuthenticated = !!localStorage.getItem('token');
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }
  const userRole = user?.role;
  const crmAccess = useCRMAccess(user);

  // Helper to check management roles
  const isManagement = ['HR', 'ADMIN', 'MANAGER', 'ACCOUNTS', 'TEAM LEAD'].includes(userRole);
  // Task Report calls the same endpoints as task assignment (CAN_ASSIGN on
  // the server) — ACCOUNTS is management but was never able to assign or
  // view tasks, so it's excluded here same as it already is from Assign Task.
  const canSeeTaskReport = ['HR', 'ADMIN', 'MANAGER', 'TEAM LEAD'].includes(userRole);
  // The Performance board scores people over time, which is review material
  // rather than day-to-day team running — narrower than Task Report on
  // purpose, and matched by the same gate on /api/analytics.
  const canSeePerformance = ['ADMIN', 'HR'].includes(userRole);
  // One person's drill-in is also open to Managers and Team Leads (reached
  // from a chat profile's Tasks tab); the server holds a Team Lead to their
  // own team.
  const canSeeEmployeePerformance = ['ADMIN', 'HR', 'MANAGER', 'TEAM LEAD'].includes(userRole);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/login" element={<Login />} />

        {/*
          * The external participant portal (feature draft Module 2).
          *
          * Outside ProtectedRoute AND outside DashboardLayout, both
          * deliberately. A vendor has no staff login, so the guard would bounce
          * them to /login and the link would never open; and the layout would
          * wrap their one conversation in a sidebar full of Attendance,
          * Payroll and Employees, every item of which is a 401 they cannot
          * read. The page owns its own chrome and its own auth - see
          * utils/portalApi.js.
          */}
        {/* Outside guests: an invitation link, or just the meeting code. No login, no sidebar. */}
        <Route path="/meet/:token" element={<GuestMeeting />} />
        <Route path="/portal/:token" element={<Portal />} />

        {/* Guests (guest role): their own sign-in and home - only the groups,
            meetings and profile they have. Same reasons as the portal above. */}
        <Route path="/guest/login" element={<GuestLogin />} />
        <Route path="/guest/*" element={<ScreenRecordingProvider><GuestHome /></ScreenRecordingProvider>} />

        <Route element={<ProtectedRoute isAllowed={isAuthenticated} />}>

          {/* ALL PAGES INSIDE HERE GET THE TOPBAR & SIDEBAR */}
          <Route element={<DashboardLayout />}>
            {/* Standard Employee Routes */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/my-inventory" element={<MyInventory />} />
            <Route path="/calendar" element={<CalendarPage />} />
            {/* Pilot only: the page sends everyone the server does not admit back to the dashboard. */}
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/meetings/:id/room" element={<MeetingRoom />} />
            <Route path="/meetings/join/:code" element={<MeetingJoinLink />} />
            <Route path="/leaves" element={<Leaves />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/wfh" element={<WorkFromHome />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/add-expense" element={<AddExpense />} />
            <Route path="/edit-expense/:id" element={<EditExpense />} />
            <Route path="/reimbursements" element={<Reimbursements />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/my-tasks" element={<MyTasks />} />
            {/* Daily reports were removed: old links and notifications land on My Tasks. */}
            <Route path="/daily-report" element={<Navigate to="/my-tasks" replace />} />
            <Route path="/task/:id" element={<TaskDetail />} />
            {/* The employee view of the payroll page. /payroll picks its view
                from the role, so for ADMIN/HR it is the company register and
                their own payslips had nowhere to live. */}
            <Route path="/my-payroll" element={<Payroll personal />} />
            {/* Help & Training: onboarding videos and FAQ. Every signed-in role. */}
            <Route path="/help" element={<HelpTraining />} />
            {/* GTS Academy. Hidden unless the server allows it (ACADEMY_MODE). */}
            <Route path="/academy" element={<Academy />} />

            {/* The project workspace (feature draft Module 1), employee half.
                Open to every role: the list is scoped to projects you are
                actually on, and the detail page is refused by the server for
                anything else — so there is nothing here to gate by role. */}
            <Route path="/my-projects" element={<MyProjects />} />
            <Route path="/my-projects/:id" element={<MyProjectDetail />} />

            {/* Groups & internal chat (feature draft Module 3). Every role —
                management's rule is that anyone may message anyone. The
                /join/:token route is the group invite link. */}
            <Route path="/chats" element={<Chats />} />
            <Route path="/chats/join/:token" element={<Chats />} />
            <Route path="/chats/:id" element={<Chats />} />
            {/* Vendors. Inside the layout and the guard - this is staff-facing;
                the vendors themselves never see it. Open to every employee: the
                server shows each one only the vendors they manage. */}
            <Route path="/external-users" element={<ExternalUsers />} />

            {/* 👇 Management Routes (Admin, HR, Manager, Accounts) */}
            <Route path="/crm" element={!crmAccess.loaded ? <p role="status">Checking CRM access…</p> : crmAccess.enabled ? <CRM key="workspace" /> : <Navigate to="/dashboard" replace />} />
            <Route path="/crm/outreach" element={<CRMOutreach />} />
            <Route path="/crm/contacts/:contactId" element={!crmAccess.loaded ? <p role="status">Checking CRM access…</p> : crmAccess.enabled ? <CRM key="contact-record" /> : <Navigate to="/dashboard" replace />} />
            <Route path="/employees" element={isManagement ? <Employees /> : <Navigate to="/dashboard" />} />
            <Route path="/add-employee" element={isManagement ? <AddEmployee /> : <Navigate to="/dashboard" />} />
            <Route path="/employee/:id" element={isManagement ? <EmployeeProfile /> : <Navigate to="/dashboard" />} />
            <Route path="/edit-employee/:id" element={isManagement ? <EditEmployee /> : <Navigate to="/dashboard" />} />
            <Route path="/attendance-logs" element={isManagement ? <AttendanceLogs /> : <Navigate to="/dashboard" />} />
            <Route path="/raw-punches" element={isManagement ? <RawPunches /> : <Navigate to="/dashboard" />} />
            <Route path="/absent-employees" element={isManagement ? <AbsentEmployees /> : <Navigate to="/dashboard" />} />
            <Route path="/Employee-requests" element={isManagement ? <EmployeeRequests /> : <Navigate to="/dashboard" />} />
            <Route path="/projects" element={isManagement ? <Projects /> : <Navigate to="/dashboard" />} />
            {/* The management half of the workspace, reached from the registry.
                An employee who follows a link here is sent to their own project
                list rather than the dashboard — same project, the shell they
                are allowed. The server still decides whether they may read it. */}
            <Route
                path="/projects/:id"
                element={isManagement ? <ProjectWorkspace /> : <Navigate to="/my-projects" />}
            />
            <Route path="/tasks" element={isManagement ? <Tasks /> : <Navigate to="/dashboard" />} />
            <Route path="/task-report" element={canSeeTaskReport ? <TaskReport /> : <Navigate to="/dashboard" />} />
            <Route path="/daily-reports" element={<Navigate to={canSeeTaskReport ? '/task-report' : '/dashboard'} replace />} />
            <Route path="/performance" element={canSeePerformance ? <Performance /> : <Navigate to="/dashboard" />} />
            <Route path="/performance/:id" element={canSeeEmployeePerformance ? <EmployeePerformance /> : <Navigate to="/dashboard" />} />
            <Route path="/add-task" element={isManagement ? <AddTask /> : <Navigate to="/dashboard" />} />
            <Route path="/add-task/voice" element={isManagement ? <VoiceAssignTask /> : <Navigate to="/dashboard" />} />
            {/* Open to anyone signed in, not just management: an assignee is
                notified about the schedule's discussion and the notification
                links straight here, so gating the route bounced them to the
                dashboard. The server already limits the data to people on the
                schedule, and the page hides the management actions. */}
            <Route path="/tasks/recurring/:id" element={<RecurringTaskDetail />} />
            <Route path="/tasks/recurring/:id/edit" element={isManagement ? <EditRecurringTask /> : <Navigate to="/dashboard" />} />
            <Route path="/edit-task/:id" element={isManagement ? <EditTask /> : <Navigate to="/dashboard" />} />
            
            {/* Expense & Inventory Management */}
            <Route path="/admin-expenses" element={isManagement ? <AdminExpenses /> : <Navigate to="/dashboard" />} />
            <Route path="/all-expenses" element={isManagement ? <AllExpenses /> : <Navigate to="/dashboard" />} />
            <Route path="/inventory" element={isManagement ? <Inventory /> : <Navigate to="/dashboard" />} />
            <Route path="/add-inventory" element={isManagement ? <AddInventory /> : <Navigate to="/dashboard" />} />
            <Route path="/edit-inventory/:id" element={isManagement ? <EditInventory /> : <Navigate to="/dashboard" />} />

            {/* LOCKED TO ADMIN ONLY */}
            <Route path="/admin-settings" element={userRole === 'ADMIN' ? <AdminSettings /> : <Navigate to="/dashboard" />} />
            <Route path="/whatsapp" element={userRole === 'ADMIN' ? <WhatsAppAdmin /> : <Navigate to="/dashboard" />} />
            <Route path="/usage-report" element={userRole === 'ADMIN' ? <UsageReport /> : <Navigate to="/dashboard" />} />
            <Route path="/integrations" element={userRole === 'ADMIN' ? <Integrations /> : <Navigate to="/dashboard" />} />
            <Route path="/notifications-sent" element={<Navigate to={userRole === 'ADMIN' ? '/notifications?tab=sent' : '/notifications'} replace />} />

            {/* LOCKED TO HR & ADMIN */}
            <Route path="/admin-chat" element={(userRole === 'HR' || userRole === 'ADMIN') ? <AdminChat /> : <Navigate to="/dashboard" />} />
            <Route path="/payroll" element={<Payroll />} />

          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
